/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import type {
  ChannelType,
  Message,
  PackageStats,
  ParsedAttachment,
  ProcessedChannel,
} from "@/types";
import JSONbig from "json-bigint";
import JSZip from "jszip";
import { DateRange } from "react-day-picker";

const JSONbigParser = JSONbig({ storeAsString: true });

interface RawChannelData {
  id: string;
  type: string;
  name?: string;
  recipients?: string[];
  guild?: {
    id: string;
    name: string;
  };
  icon_hash?: string;
}

interface ProcessedData {
  channels: ProcessedChannel[];
  stats: PackageStats;
}

function parseAttachments(attachmentsString?: string): ParsedAttachment[] {
  if (!attachmentsString) return [];

  return attachmentsString
    .split(/[\s,]+/) // Split on whitespace or commas
    .filter(Boolean)
    .map((url) => {
      try {
        const cleanUrl = url.trim();
        const urlObj = new URL(cleanUrl);
        const filename = urlObj.pathname.split("/").pop() || "attachment";
        return {
          url: cleanUrl,
          filename,
        };
      } catch {
        console.warn("Invalid attachment URL:", url);
        return null;
      }
    })
    .filter(
      (attachment): attachment is ParsedAttachment => attachment !== null,
    );
}

export async function processDataPackage(file: File): Promise<ProcessedData> {
  const zip = await JSZip.loadAsync(file, {
    createFolders: false,
  });

  const indexFile = zip.file("Messages/index.json");
  if (!indexFile) {
    throw new Error("Messages/index.json not found in the ZIP archive.");
  }

  const indexContent = await indexFile.async("string");
  const index = JSONbigParser.parse(indexContent) as Record<string, string>;

  const stats: PackageStats = {
    totalMessages: 0,
    totalAttachments: 0,
    uniqueServers: new Set<string>(),
    channelTypes: new Set<string>(),
    serverCount: 0,
    channelCount: 0,
    dateRange: {
      start: new Date(),
      end: new Date(),
    },
  };

  const processedChannels: ProcessedChannel[] = [];
  const allMessageDates: Date[] = [];

  // To determine the current user ID from DM channels
  const dmChannelsRecipients: {
    channelId: string;
    recipients: string[];
  }[] = [];

  // Process channels in smaller chunks
  const BATCH_SIZE = 5;
  const channelIds = Object.keys(index);

  for (let i = 0; i < channelIds.length; i += BATCH_SIZE) {
    const batchIds = channelIds.slice(i, i + BATCH_SIZE);

    try {
      const batchResults = await Promise.all(
        batchIds.map(async (channelId) => {
          const channelPath = `Messages/c${channelId}/channel.json`;
          const messagesPath = `Messages/c${channelId}/messages.json`;

          try {
            // Load files one at a time to reduce memory usage
            const channelFile = zip.file(channelPath);
            if (!channelFile) return null;
            const channelContent = await channelFile.async("string");
            const channelData = JSONbigParser.parse(
              channelContent,
            ) as RawChannelData;

            const messagesFile = zip.file(messagesPath);
            if (!messagesFile) return null;
            const messagesContent = await messagesFile.async("string");
            const messages = JSONbigParser.parse(messagesContent) as Message[];

            if (messages.length === 0) return null;

            // Process messages in chunks to avoid stack overflow and argument limit
            const messageDates: Date[] = [];
            const chunkSize = 1000;

            for (let j = 0; j < messages.length; j += chunkSize) {
              const messageChunk = messages.slice(j, j + chunkSize);
              // Process each message individually instead of using spread
              messageChunk.forEach((msg) => {
                const date = new Date(msg.Timestamp);
                messageDates.push(date);
                allMessageDates.push(date);
              });
            }

            // Store DM channel recipients for later analysis
            if (
              channelData.type === "DM" &&
              channelData.recipients &&
              channelData.recipients.length === 2
            ) {
              dmChannelsRecipients.push({
                channelId: channelData.id,
                recipients: channelData.recipients,
              });
            }

            return {
              channelData,
              messages,
              rawName: index[channelId],
            };
          } catch (error) {
            console.error(`Error processing channel ${channelId}:`, error);
            return null;
          }
        }),
      );

      // Process valid results
      batchResults.forEach((result) => {
        if (!result) return;

        const { channelData, messages, rawName } = result;
        const processedChannel = processChannel(channelData, rawName, messages);

        stats.totalMessages += messages.length;
        stats.totalAttachments += messages.filter((m) => m.Attachments).length;
        stats.channelTypes.add(channelData.type);

        if (processedChannel.serverName) {
          stats.uniqueServers.add(processedChannel.serverName);
        }

        processedChannels.push(processedChannel);
      });

      // Allow garbage collection between batches
      await new Promise((resolve) => setTimeout(resolve, 0));
    } catch (error) {
      console.error(`Error processing batch:`, error);
    }
  }

  // Sort channels by message count
  processedChannels.sort((a, b) => b.messages.length - a.messages.length);

  stats.channelCount = processedChannels.length;
  stats.serverCount = stats.uniqueServers.size;

  // Analyze DM channels to determine user IDs
  let currentUserId: string | undefined;

  // First, try to determine the current user ID from the DM channels
  if (dmChannelsRecipients.length > 0) {
    // Count occurrences of each recipient ID across DM channels
    const recipientCounts = new Map<string, number>();

    dmChannelsRecipients.forEach(({ recipients }) => {
      recipients.forEach((id) => {
        if (id !== "Deleted User") {
          recipientCounts.set(id, (recipientCounts.get(id) || 0) + 1);
        }
      });
    });

    // If we have multiple DM channels, the most frequent ID is likely the current user
    if (dmChannelsRecipients.length > 1) {
      let maxCount = 0;

      recipientCounts.forEach((count, id) => {
        if (count > maxCount) {
          maxCount = count;
          currentUserId = id;
        }
      });
    } else if (dmChannelsRecipients.length === 1) {
      // If we only have one DM channel, we can only determine the current user ID
      // if one of the recipients is "Deleted User"
      const singleDM = dmChannelsRecipients[0];
      if (singleDM.recipients.includes("Deleted User")) {
        // The non-deleted user must be the current user
        currentUserId = singleDM.recipients.find((id) => id !== "Deleted User");
      }
    }
  }

  // Once we have the current user ID, assign recipientId to DM channels
  if (currentUserId) {
    processedChannels.forEach((channel) => {
      if (channel.type === "DM" && channel.metadata.recipients) {
        // Assign the current user ID to all DM channels
        channel.currentUserId = currentUserId;

        // Find the other recipient (not the current user)
        const otherRecipient = channel.metadata.recipients.find(
          (id) => id !== currentUserId && id !== "Deleted User",
        );
        if (otherRecipient) {
          channel.recipientId = otherRecipient;
        }
      }
    });
  }

  // Calculate date range in chunks to avoid stack overflow
  if (allMessageDates.length > 0) {
    let minDate = allMessageDates[0];
    let maxDate = allMessageDates[0];

    const dateChunkSize = 1000;
    for (let i = 0; i < allMessageDates.length; i += dateChunkSize) {
      const chunk = allMessageDates.slice(i, i + dateChunkSize);
      const chunkMin = new Date(Math.min(...chunk.map((d) => d.getTime())));
      const chunkMax = new Date(Math.max(...chunk.map((d) => d.getTime())));

      if (chunkMin < minDate) minDate = chunkMin;
      if (chunkMax > maxDate) maxDate = chunkMax;
    }

    stats.dateRange = {
      start: minDate,
      end: maxDate,
    };
  }

  // Clean up
  zip.files = {};

  return {
    channels: processedChannels,
    stats,
  };
}

function processChannel(
  channelData: RawChannelData,
  rawName: string,
  messages: Message[],
): ProcessedChannel {
  const dates = getMessageDateRange(messages);

  const processed: ProcessedChannel = {
    id: channelData.id,
    type: channelData.type as ChannelType,
    displayName: "",
    originalName: rawName,
    messages: messages.map((msg) => ({
      ...msg,
      parsedAttachments: parseAttachments(msg.Attachments),
    })),
    metadata: {
      ...channelData,
      rawIndexName: rawName,
    },
    searchableText: "",
    messageCount: messages.length,
    attachmentCount: messages.reduce(
      (sum, msg) => sum + parseAttachments(msg.Attachments).length,
      0,
    ),
    dateRange: {
      start: dates?.from || new Date(),
      end: dates?.to || new Date(),
    },
  };

  if (channelData.type === "DM") {
    const dmMatch = rawName.match(/^Direct Message with (.+?)(?:#0)?$/);
    const username = dmMatch
      ? dmMatch[1]
      : channelData.recipients?.[0] || "Unknown User";

    processed.displayName = `Direct Message with ${username}`;
    processed.participants = [username];
    processed.channelCategory = "Direct Messages";
  } else if (channelData.type === "GROUP_DM") {
    if (rawName.startsWith("Direct Message with ")) {
      const participantsStr = rawName.replace("Direct Message with ", "");
      const participants = participantsStr
        .split(", ")
        .map((p) => p.replace(/#0$/, ""));
      processed.displayName = `Group DM with ${participants.join(", ")}`;
      processed.participants = participants;
    } else {
      processed.displayName =
        channelData.name ||
        `Group DM with ${channelData.recipients?.join(", ") || "Unknown Users"}`;
      processed.participants = channelData.recipients;
    }
    processed.channelCategory = "Group Messages";
  } else if (channelData.guild) {
    processed.serverName = channelData.guild.name;

    if (rawName.startsWith("Unknown channel in")) {
      processed.displayName = "Unknown Channel";
      if (channelData.name) {
        processed.displayName = channelData.name;
      }
    } else {
      const categoryMatch = rawName.match(/^(.+?)(?: in (.+))?$/);
      if (categoryMatch) {
        const [, channelName, category] = categoryMatch;
        processed.displayName = channelName.trim();
        if (category && category !== channelData.guild.name) {
          processed.channelCategory = category.trim();
        }
      } else {
        processed.displayName = rawName;
      }
    }
  } else {
    processed.displayName = rawName;
  }

  processed.searchableText = sanitizeForSearch(
    [
      processed.displayName,
      processed.serverName,
      processed.channelCategory,
      processed.participants?.join(" "),
      processed.originalName,
      messages.map((m) => sanitizeForSearch(m.Contents)).join(" "),
    ]
      .filter(Boolean)
      .join(" "),
  );

  return processed;
}

export function getMessageDateRange(
  messages: Message[],
): DateRange | undefined {
  if (messages.length === 0) return undefined;

  const timestamps = messages.map((m) => new Date(m.Timestamp));
  const minDate = new Date(Math.min(...timestamps.map((t) => t.getTime())));
  const maxDate = new Date(Math.max(...timestamps.map((t) => t.getTime())));

  return {
    from: minDate,
    to: maxDate,
  };
}

export function sanitizeForSearch(text: string | undefined): string {
  if (!text) return "";

  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function findMatchesInText(
  haystack: string,
  needle: string,
): [number, number][] {
  const sanitizedHaystack = sanitizeForSearch(haystack);
  const sanitizedNeedle = sanitizeForSearch(needle);
  const matches: [number, number][] = [];

  let index = 0;
  while ((index = sanitizedHaystack.indexOf(sanitizedNeedle, index)) !== -1) {
    matches.push([index, index + sanitizedNeedle.length]);
    index += sanitizedNeedle.length;
  }

  return matches;
}

export function highlightMatches(
  originalText: string,
  matches: [number, number][],
): { text: string; isHighlight: boolean }[] {
  if (!matches.length) return [{ text: originalText, isHighlight: false }];

  const result: { text: string; isHighlight: boolean }[] = [];
  let lastIndex = 0;

  matches.forEach(([start, end]) => {
    if (start > lastIndex) {
      result.push({
        text: originalText.slice(lastIndex, start),
        isHighlight: false,
      });
    }
    result.push({
      text: originalText.slice(start, end),
      isHighlight: true,
    });
    lastIndex = end;
  });

  if (lastIndex < originalText.length) {
    result.push({
      text: originalText.slice(lastIndex),
      isHighlight: false,
    });
  }

  return result;
}
